import {
  Button,
  Link,
  MainNavigation,
  MainNavigationItem,
  SubNavigation,
  SubNavigationContent,
  SubNavigationHeader,
  SubNavigationItem,
  SubNavigationList,
  Text,
  Toggle,
  useMainNavigation,
  User,
} from '@wfp/react';
import { get } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useLocation } from 'react-router-dom';

import { msalApp, msSignOut } from 'src/auth/auth';
import { API_HOST } from 'src/constants';
import { resetCustomLoginDataRequest } from 'src/redux/auth/actions';
import { apiCallAsync } from 'src/redux/sagas';
import { updateUserSettings } from 'src/redux/view/actions';
import { UPDATE_USER_SETTINGS_KEY } from 'src/redux/view/constants';
import { getActionAPISource, getAppUri } from 'src/utils';

function HQAdminLinks(props) {
  const location = useLocation();

  const { onChangeSub, activeMenuItem } = useMainNavigation();

  function closeSubMenu() {
    if (onChangeSub && activeMenuItem) {
      onChangeSub('close', activeMenuItem);
    }
  }

  // Close the submenu when the pathname changes
  useEffect(() => {
    closeSubMenu();
  }, [location.pathname]);

  return (
    <SubNavigation className="hqadmin-submenu">
      <SubNavigationHeader>HQ Admin</SubNavigationHeader>
      <SubNavigationContent>
        <SubNavigationList>
          {getActionAPISource('ADMIN_DASHBOARD') && (
            <SubNavigationItem>
              <NavLink
                to={props.routesConfig.root.sub_routes.admin_dashboard.path}
                data-test-id="admin-dashboard-link"
              >
                Administration dashboard
              </NavLink>
            </SubNavigationItem>
          )}

          {getAppUri('users-management') && (
            <SubNavigationItem>
              <NavLink
                to={props.routesConfig.root.sub_routes.users_management.path}
                data-test-id="user-management-link"
              >
                Users Management
              </NavLink>
            </SubNavigationItem>
          )}

          <SubNavigationItem>
            <Link
              href={`${API_HOST}/oidc/`}
              target="_blank"
              data-test-id="annual-report-link"
              onClick={closeSubMenu}
            >
              Django backend
            </Link>
          </SubNavigationItem>
        </SubNavigationList>
      </SubNavigationContent>
    </SubNavigation>
  );
}

export function SignOutBox() {
  const appConfig = useSelector(
    state => state.viewData?.GET_APP_CONFIGURATION?.data,
  );
  const customLoginData = useSelector(state => state.auth);
  const isUpdatingSettings = useSelector(
    state => state.viewData?.[UPDATE_USER_SETTINGS_KEY]?.isFetching || false,
  );
  const userFullname = appConfig?.userInfo?.full_name;
  const [account, setAccount] = useState(null);
  const [isLogoutPending, setIsLogoutPending] = useState(false);
  const emailNotificationsEnabled =
    appConfig?.userInfo?.user_settings?.send_email_on_comments || false;

  const dispatch = useDispatch();

  useEffect(() => {
    const accounts = msalApp.getAllAccounts();
    if (accounts && accounts.length === 1) {
      setAccount(accounts[0]);
    }
  }, []);

  const userUsername = useMemo(() => {
    if (userFullname) {
      return userFullname;
    }
    if (account && account.username) {
      return account.username;
    }
    return 'Anonymous';
  }, [userFullname, account]);

  async function handleSignOut() {
    if (customLoginData?.accessToken) {
      dispatch(resetCustomLoginDataRequest());
      return;
    }

    setIsLogoutPending(true);
    try {
      await msSignOut();
    } catch (error) {
      console.error('Errors during the lgoout:', error);
    } finally {
      setIsLogoutPending(false);
    }
  }

  const handleEmailNotificationToggle = toggled => {
    dispatch(updateUserSettings({ send_email_on_comments: toggled }));
  };

  return (
    <SubNavigation>
      <SubNavigationHeader className="user-header">
        <div className="user-header-wrapper">
          <div data-test-id="welcome-message">{userUsername}</div>
          <Button
            data-test-id="logout-button"
            onClick={handleSignOut}
            small
            disabled={isLogoutPending}
          >
            Logout
          </Button>
        </div>
        <div className="user-header-wrapper notifications-area">
          <div className="user-header-wrapper-inner">
            <Text className="email-notifications-text">
              Email notifications for comments
            </Text>
            <Toggle
              id="email-notifications-toggle"
              toggled={emailNotificationsEnabled}
              onToggle={handleEmailNotificationToggle}
              disabled={isUpdatingSettings}
              size="sm"
              labelA=""
              labelB=""
            />
          </div>
          <div>
            <Text>
              Enable this setting to receive email notifications for any new
              reply to comment threads you participate in, as well as when the
              comment gets closed.
            </Text>
          </div>
        </div>
      </SubNavigationHeader>
    </SubNavigation>
  );
}

export const Header = props => {
  const [profilePhotoUrl, setProfilePhotoUrl] = useState('');

  async function getUserPicture() {
    const graphUrl = 'https://graph.microsoft.com/v1.0/me/photo/$value';

    await apiCallAsync(graphUrl, {}, 'get', 'blob')
      .then(response => {
        return response.status === 200 ? response.data : null;
      })
      .then(blobData => {
        setProfilePhotoUrl(blobData ? URL.createObjectURL(blobData) : '');
      })
      .catch(e => {
        console.log(e);
      });
  }

  const mainNavigationProps = {
    className: 'custom-main-navigation',
    pageWidth: 'lg',
    logo: (
      <NavLink to="/" data-test-id="spring-main-link">
        <Text>SPRiNG</Text>
      </NavLink>
    ),
  };

  useEffect(() => {
    getUserPicture();
  }, []);

  const roles = get(window, 'djconfig.userInfo.roles', []);
  const isHQAdmin = roles.includes('hq_admin') ? true : false;
  const superuser = get(window, 'djconfig.userInfo.superuser', []);

  const envName = process.env.ACR_ENV || 'LOCAL';
  const envClass = 'environment-indicator ' + envName.toLowerCase();
  let envNotification = null;
  if (envName !== 'PROD') {
    envNotification = <div className={envClass}>{envName}</div>;
  }
  if (get(window, 'djconfig.userInfo.hasAnyPermission', false)) {
    return (
      <>
        <MainNavigation {...mainNavigationProps}>
          <MainNavigationItem>
            <NavLink
              className="wfp--link acr-dashboard-link"
              to="/"
              data-test-id="acr-dashboard-link"
            >
              ACR Dashboard
            </NavLink>
          </MainNavigationItem>

          {(isHQAdmin || superuser) && (
            <MainNavigationItem
              className="wfp--main-navigation__hq-admin"
              subNavigation={<HQAdminLinks routesConfig={props.routesConfig} />}
            >
              <div data-test-id="hq-admin-link">HQ Admin</div>
            </MainNavigationItem>
          )}

          <MainNavigationItem
            className="wfp--main-navigation__user"
            subNavigation={<SignOutBox />}
          >
            <User
              ellipsis="true"
              data-test-id="user-icon"
              image={profilePhotoUrl}
            />
          </MainNavigationItem>
        </MainNavigation>
        {envNotification}
      </>
    );
  }

  // If no permissions
  return (
    <MainNavigation {...mainNavigationProps}>
      <MainNavigationItem
        className="wfp--main-navigation__user"
        subNavigation={<SignOutBox />}
      >
        <User ellipsis="true" data-test-id="user-icon" />
      </MainNavigationItem>
    </MainNavigation>
  );
};

export default props => <Header {...props} />;
